import type { Book, ChapterDetails } from "#/types";

import { deleteChapters, getBook, getChapterDetails, updateProgress } from "#/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCachedChapter, updateChapterCache } from "./chapter-store";

export function useChapter() {
  const { id, chapterId } = useParams<{ id: string; chapterId: string }>();
  const [chapter, setChapter] = useState<ChapterDetails | null>(null);
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (id && chapterId) {
      const bid = parseInt(id);
      const cid = parseInt(chapterId);

      // Check cache first
      const cached = getCachedChapter(bid, cid);
      if (cached) {
        setChapter(cached);
        setLoading(false);
        // Still fetch book if not present or just assume it's fine for now.
        // But for simplicity, we still Promise.all or handle book separately.
      }

      setLoading(true);
      Promise.all([cached ? Promise.resolve(cached) : getChapterDetails(bid, cid), getBook(id)])
        .then(([chapterData, bookData]) => {
          setChapter(chapterData);
          setBook(bookData);
          if (!cached) {
            updateChapterCache(bid, cid, chapterData);
          }
          setError(null);
          // Scroll to top on chapter change
          window.scrollTo(0, 0);

          // Update reading progress
          void updateProgress(id, chapterData.id);
        })
        .catch((error) => {
          setError(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, chapterId]);

  const goToNext = () => {
    if (chapter?.next_id) {
      void navigate(`/book/${id}/chapter/${chapter.next_id}`);
    }
  };

  const goToPrev = () => {
    if (chapter?.prev_id) {
      void navigate(`/book/${id}/chapter/${chapter.prev_id}`);
    }
  };

  const handleDeleteFromHere = async () => {
    if (!id || !chapterId || !chapter) {
      return;
    }
    if (confirm("Are you sure you want to delete this and all subsequent chapters?")) {
      try {
        await deleteChapters(id, parseInt(chapterId));
        void navigate(`/book/${id}`);
      } catch (error: any) {
        alert("Failed to delete chapters: " + error.message);
      }
    }
  };

  return {
    chapter,
    book,
    loading,
    error,
    goToNext,
    goToPrev,
    handleDeleteFromHere,
    bookId: id,
  };
}
